import { useMemo, useRef, useState, useCallback } from 'react';
import { apiService } from '../services/api';
import { useAppStore } from '../store/appStore';
import { PermissionLevel, levelRank } from '../constants/permissionLevels';
import { TokenManager } from '../services/token-manager';

/**
 * Reads the leveled business permission schema embedded in the current phase-2 token — no
 * network round trip. Intended as the go-forward replacement for ad-hoc
 * `businessRole === 'admin' || 'owner'` checks scattered through the app; existing checks are
 * left untouched (see the Users/Departments/Permissions plan) but new screens should use this.
 */
export function usePermissions() {
  // Subscribing to tokens re-derives permissions whenever the store's token changes
  // (login, select-business, refresh) without a separate subscription mechanism.
  const tokens = useAppStore(state => state.tokens);
  const setTokens = useAppStore(state => state.setTokens);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastRefreshAtRef = useRef(0);

  const permissions = useMemo<Record<string, PermissionLevel>>(() => {
    return apiService.getPermissionsFromToken() || {};
  }, [tokens?.token]);

  const hasPermissionAtLeast = (key: string, minLevel: PermissionLevel): boolean => {
    const level = permissions[key] || 'none';
    return levelRank(level) >= levelRank(minLevel);
  };

  /**
   * Forces the access token to be re-minted now (rather than waiting for the next 401 or
   * the token's own ~1h expiry) — the backend's /auth/refresh handler already re-runs
   * BusinessPermissionResolutionService.resolveForUser on every refresh (see
   * internal-auth-provider.ts's refreshToken), so this is the correct way to pick up a
   * permission grant an admin just changed without forcing a full logout/login.
   * tokenManager.refreshTokens() itself only updates apiService's in-memory token; it
   * never touches the Zustand store, so without the explicit setTokens() call below
   * usePermissions' own `tokens` subscription (and therefore every
   * hasPermissionAtLeast-gated screen) would keep re-deriving permissions from the OLD
   * token despite the network refresh having succeeded — this was the actual root cause
   * behind "permissions only work at admin level": an owner/admin's permissions are
   * maxed regardless of token freshness, while a View/Manager grant only becomes visible
   * once this refresh actually lands in the store. Deliberately uses the lightweight
   * setTokens action (not updateAuthTokens/setAuthState) since this is a background
   * permission-freshness refresh, not a login/business-switch — it shouldn't flip
   * isAuthInitializing or otherwise disturb unrelated auth-state-dependent UI.
   * Debounced to once per 10s so multiple screens mounting at once don't all fire it.
   */
  const refreshPermissions = useCallback(async (): Promise<boolean> => {
    const now = Date.now();
    if (now - lastRefreshAtRef.current < 10_000) return true;
    lastRefreshAtRef.current = now;
    setIsRefreshing(true);
    try {
      const result = await TokenManager.getInstance().refreshTokens();
      if (result) {
        setTokens({ token: result.token, refreshToken: result.refreshToken });
        return true;
      }
      return false;
    } catch (error) {
      console.warn('[usePermissions] Failed to refresh permissions:', error);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [setTokens]);

  return { permissions, hasPermissionAtLeast, refreshPermissions, isRefreshing, loading: false };
}
